import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment-timezone';

import { DENSITY } from '../constants';
import { initialsOf } from '../session-model';
import SessionCard from './SessionCard';

const WEEKEND_DAY_NUMBERS = [0, 6];
const STACK_GAP_PX = 4;

/** Flat minimum for detailed week cards when estimating before measure. */
const COMPACT_MIN_CARD_HEIGHT_PX = 88;

/** Hours kept above the first session and below the last when not showing the full day. */
const WEEK_DISPLAY_PAD_HOURS = 1;

/** Same pixel height for every hour, on every day. */
const WEEK_ROW_HEIGHT_PX = 40;

/** Compact week chip row height (time label + initials tile). */
const CHIP_ROW_HEIGHT_PX = 34;

/** Gap between compact chip rows / wrapped chip lines. */
const CHIP_ROW_GAP_PX = 4;

/**
 * How many chips typically fit on one line in a day column
 * (column ~130px minus the time label). Keep low so wrapped
 * lines reserve enough vertical space and do not overlap.
 */
const CHIPS_PER_LINE_ESTIMATE = 2;

/** Estimated height when many same-time chips wrap onto extra lines. */
function estimateChipRowHeightPx(chipCount) {
  const lines = Math.max(1, Math.ceil(chipCount / CHIPS_PER_LINE_ESTIMATE));
  return lines * CHIP_ROW_HEIGHT_PX + (lines - 1) * CHIP_ROW_GAP_PX;
}

/**
 * Estimate week-detailed card height from the content it always renders
 * (tutor, time, subjects, and every student row with status meta — no +N).
 */
function estimateWeekDetailedCardHeightPx(session) {
  const padding = 16;
  const tutor = session.tutorName ? 18 : 0;
  const head = 18;
  const subjects = Array.from(
    new Set((session.students || []).map((student) => student.subjectName).filter(Boolean))
  );
  const subjectLine = subjects.length > 0 ? 16 : 0;
  const students = session.students || [];
  const studentBlockGap = 4;
  let studentsHeight = 22; // empty state
  if (students.length > 0) {
    studentsHeight = students.reduce((total, student, index) => {
      const hasExtraTag = Boolean(student.isReassigned || student.isPaused);
      const row =
        16 + // name line
        (student.subjectName ? 12 : 0) +
        (hasExtraTag ? 36 : 22) +
        (index > 0 ? studentBlockGap : 0);
      return total + row;
    }, 0);
  }
  return padding + tutor + head + subjectLine + 4 + studentsHeight;
}

function minCardHeightForSession(session, density, measuredPx) {
  const estimated =
    density === DENSITY.COMPACT
      ? COMPACT_MIN_CARD_HEIGHT_PX
      : estimateWeekDetailedCardHeightPx(session);
  return Math.max(estimated, measuredPx || 0);
}

function hourLabel(hour) {
  return `${String(hour).padStart(2, '0')}:00`;
}

function sessionEndHour(session) {
  if (session.endHour != null && session.endHour > session.startHour) {
    return session.endHour;
  }
  return session.startHour + 1;
}

/**
 * Sessions for one day, earliest start first, with a content height for each card.
 */
function layoutDaySessions(sessions, windowStart, windowEnd, density, measuredHeights) {
  return [...sessions]
    .map((session) => {
      const start = Math.max(session.startHour, windowStart);
      const end = Math.min(sessionEndHour(session), windowEnd);
      return {
        session,
        start,
        end,
        minCardPx: minCardHeightForSession(
          session,
          density,
          measuredHeights ? measuredHeights[session.key] : 0
        ),
      };
    })
    .sort((a, b) => {
      const startDiff = a.start - b.start;
      if (startDiff !== 0) {
        return startDiff;
      }
      return a.end - b.end;
    });
}

/** Round a decimal hour to the nearest minute for stable same-time grouping. */
function startMinuteKey(startHour) {
  return Math.round(startHour * 60);
}

/**
 * Detailed week: one band per distinct start minute.
 * Same-minute sessions stack in that band; other times get their own band.
 */
function buildDetailedTimeGroups(items, dateKey, measuredRowHeights) {
  const byStart = new Map();
  items.forEach((item) => {
    const key = String(startMinuteKey(item.start));
    if (!byStart.has(key)) {
      byStart.set(key, {
        key,
        start: item.start,
        timeLabel: item.session.startTimeLabel || formatChipTimeLabel('', item.start),
        items: [],
      });
    }
    byStart.get(key).items.push(item);
  });

  return Array.from(byStart.values())
    .sort((a, b) => a.start - b.start || startMinuteKey(a.start) - startMinuteKey(b.start))
    .map((group) => {
      const measureKey = `${dateKey}|${group.key}`;
      const measured = measuredRowHeights ? measuredRowHeights[measureKey] : 0;
      const contentHeight = group.items.reduce((total, item, index) => {
        return total + item.minCardPx + (index > 0 ? STACK_GAP_PX : 0);
      }, 0);
      return {
        ...group,
        height: Math.max(contentHeight, measured || 0, CHIP_ROW_HEIGHT_PX),
      };
    });
}

/**
 * Compact week: one chip row per distinct start minute.
 * Five sessions at 9:15 share one row; 9:00 and 9:30 each get their own.
 */
function buildCompactChipGroups(items, dateKey, measuredRowHeights) {
  const byStart = new Map();
  items.forEach((item) => {
    const key = String(startMinuteKey(item.start));
    if (!byStart.has(key)) {
      byStart.set(key, {
        key,
        start: item.start,
        timeLabel: item.session.startTimeLabel || formatChipTimeLabel('', item.start),
        items: [],
      });
    }
    byStart.get(key).items.push(item);
  });

  return Array.from(byStart.values())
    .sort((a, b) => a.start - b.start || startMinuteKey(a.start) - startMinuteKey(b.start))
    .map((group) => {
      const measureKey = `${dateKey}|${group.key}`;
      const measured = measuredRowHeights ? measuredRowHeights[measureKey] : 0;
      const estimated = estimateChipRowHeightPx(group.items.length);
      return {
        ...group,
        height: Math.max(CHIP_ROW_HEIGHT_PX, estimated, measured || 0),
      };
    });
}

/**
 * Stack each sub-hour time as its own band inside the hour, from the top of
 * that hour. Same-minute chips stay on one row; later minutes get the next band.
 */
function placeCompactChipGroups(groups, windowStart, heightByHour, hours) {
  const placed = [];
  const cursorByHour = new Map();

  groups.forEach((group) => {
    const hour = Math.floor(group.start);
    const hourTop = offsetAtHour(hour, windowStart, heightByHour, hours);
    if (!cursorByHour.has(hour)) {
      cursorByHour.set(hour, hourTop);
    }
    const top = cursorByHour.get(hour);
    placed.push({ ...group, top });
    cursorByHour.set(hour, top + group.height + CHIP_ROW_GAP_PX);
  });

  return placed;
}

/**
 * Each distinct start minute inside an hour needs one chip-row band.
 * Hour height is the tallest day in that hour so the shared time axis stays aligned.
 */
function resolveCompactHourHeightsPx(hours, groupsByDate) {
  const heightByHour = new Map();
  hours.forEach((hour) => heightByHour.set(hour, WEEK_ROW_HEIGHT_PX));

  hours.forEach((hour) => {
    let maxStack = 0;
    groupsByDate.forEach((groups) => {
      const inHour = groups.filter((group) => Math.floor(group.start) === hour);
      if (inHour.length === 0) {
        return;
      }
      let stack = 0;
      inHour.forEach((group, index) => {
        stack += group.height;
        if (index > 0) {
          stack += CHIP_ROW_GAP_PX;
        }
      });
      if (stack > maxStack) {
        maxStack = stack;
      }
    });
    if (maxStack > heightByHour.get(hour)) {
      heightByHour.set(hour, maxStack);
    }
  });

  return heightByHour;
}

function formatChipTimeLabel(label, startHour) {
  if (label) {
    return label;
  }
  const hour = Math.floor(startHour);
  const minute = Math.round((startHour - hour) * 60);
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Band to scroll into view for “now”: ongoing, else next upcoming, else latest past.
 */
function pickFocusTimeGroup(groups, nowHour) {
  if (!groups || groups.length === 0) {
    return null;
  }
  const enriched = groups.map((group) => {
    let end = group.start;
    (group.items || []).forEach((item) => {
      if (item.end != null && item.end > end) {
        end = item.end;
      }
    });
    return { group, start: group.start, end };
  });
  const ongoing = enriched
    .filter((entry) => entry.start <= nowHour && entry.end > nowHour)
    .sort((a, b) => a.start - b.start);
  if (ongoing.length > 0) {
    return ongoing[0].group;
  }
  const upcoming = enriched
    .filter((entry) => entry.start >= nowHour)
    .sort((a, b) => a.start - b.start);
  if (upcoming.length > 0) {
    return upcoming[0].group;
  }
  const latestPast = enriched
    .filter((entry) => entry.start < nowHour)
    .sort((a, b) => b.start - a.start);
  return latestPast.length > 0 ? latestPast[0].group : null;
}

/**
 * Operating-hours weeks show the session band only.
 * All Hours keeps the full window passed in (00:00–24:00).
 * Returns null when the week has no timed sessions and the full day is not required.
 */
function resolveWeekDisplayWindow(weekSessions, hourWindow, showFullDay) {
  if (showFullDay) {
    return { startHour: 0, endHour: 24 };
  }

  if (!weekSessions.length) {
    return null;
  }

  let minStart = Infinity;
  let maxEnd = -Infinity;
  weekSessions.forEach((session) => {
    minStart = Math.min(minStart, session.startHour);
    maxEnd = Math.max(maxEnd, sessionEndHour(session));
  });

  const startHour = Math.max(
    hourWindow.startHour,
    Math.floor(minStart) - WEEK_DISPLAY_PAD_HOURS
  );
  const endHour = Math.min(
    hourWindow.endHour,
    Math.ceil(maxEnd) + WEEK_DISPLAY_PAD_HOURS
  );

  return {
    startHour,
    endHour: Math.max(endHour, startHour + 1),
  };
}

/**
 * Pixel offset of a decimal hour. When heightByHour is set (compact week),
 * each hour can be a different height. Otherwise every hour is WEEK_ROW_HEIGHT_PX.
 */
function offsetAtHour(time, windowStart, heightByHour, hours) {
  if (!heightByHour || !hours || hours.length === 0) {
    return (time - windowStart) * WEEK_ROW_HEIGHT_PX;
  }

  let px = 0;
  for (let index = 0; index < hours.length; index += 1) {
    const hour = hours[index];
    const rowHeight = heightByHour.get(hour) || WEEK_ROW_HEIGHT_PX;
    if (time >= hour + 1) {
      px += rowHeight;
    } else if (time > hour) {
      px += (time - hour) * rowHeight;
      break;
    } else {
      break;
    }
  }
  return px;
}

/**
 * @param {Object} props
 * @param {string[]} props.weekDates Seven `YYYY-MM-DD` dates in display order.
 * @param {Object[]} props.sessions All sessions in the fetched week.
 * @param {{startHour:number, endHour:number}} props.hourWindow
 * @param {boolean} props.showFullDay All Hours: draw the full window. Otherwise trim to sessions.
 * @param {string} props.todayDate `YYYY-MM-DD` used to highlight the current column.
 */
export default function WeekView({
  weekDates,
  sessions,
  hourWindow,
  showFullDay,
  density,
  todayDate,
  onOpenSession,
  onStatusClick,
  onModeClick,
  onStudentClick,
  onTutorClick,
}) {
  const isCompact = density === DENSITY.COMPACT;
  const cardVariant = isCompact ? 'weekChip' : 'week';
  const [measuredHeights, setMeasuredHeights] = useState({});
  const [measuredChipRowHeights, setMeasuredChipRowHeights] = useState({});
  const [measureEpoch, setMeasureEpoch] = useState(0);
  const sessionNodeRefs = useRef(new Map());
  const chipRowNodeRefs = useRef(new Map());
  const weekRef = useRef(null);
  const autoScrollKeyRef = useRef('');
  const weekWidthRef = useRef(0);

  const weekSessions = useMemo(() => {
    const dates = new Set(weekDates);
    return (sessions || []).filter(
      (session) =>
        dates.has(session.date) &&
        session.startHour !== null &&
        session.startHour !== undefined
    );
  }, [sessions, weekDates]);

  const displayWindow = useMemo(
    () => resolveWeekDisplayWindow(weekSessions, hourWindow, showFullDay),
    [weekSessions, hourWindow, showFullDay]
  );

  const windowStart = displayWindow ? displayWindow.startHour : hourWindow.startHour;
  const windowEnd = displayWindow ? displayWindow.endHour : hourWindow.endHour;
  const hours = useMemo(() => {
    if (!displayWindow) {
      return [];
    }
    const list = [];
    for (let hour = windowStart; hour < windowEnd; hour += 1) {
      list.push(hour);
    }
    return list;
  }, [displayWindow, windowStart, windowEnd]);

  const sessionsByDate = useMemo(() => {
    const map = new Map();
    weekDates.forEach((date) => map.set(date, []));
    sessions.forEach((session) => {
      if (session.startHour === null || session.startHour === undefined) {
        return;
      }
      if (!map.has(session.date)) {
        return;
      }
      const endHour = sessionEndHour(session);
      if (endHour <= windowStart || session.startHour >= windowEnd) {
        return;
      }
      map.get(session.date).push(session);
    });
    return map;
  }, [sessions, weekDates, windowStart, windowEnd]);

  const sessionKeysSignature = useMemo(
    () =>
      sessions
        .map((session) => session.key)
        .filter(Boolean)
        .sort()
        .join('|'),
    [sessions]
  );

  const nowMarker = useMemo(() => {
    const timezone = sessionStorage.getItem('timezone_identifier');
    const now = timezone ? Moment().tz(timezone) : Moment();
    return {
      date: now.format('YYYY-MM-DD'),
      hour: now.hour() + now.minute() / 60 + now.second() / 3600,
    };
  }, [weekDates, density, sessionKeysSignature]);

  const dayStatsByDate = useMemo(() => {
    const map = new Map();
    weekDates.forEach((date) => {
      const daySessions = sessionsByDate.get(date) || [];
      const lessonCount = daySessions.reduce(
        (total, session) => total + (session.lessonCount || 0),
        0
      );
      map.set(date, {
        sessionCount: daySessions.length,
        lessonCount,
      });
    });
    return map;
  }, [weekDates, sessionsByDate]);

  useEffect(() => {
    setMeasuredHeights({});
    setMeasuredChipRowHeights({});
    sessionNodeRefs.current = new Map();
    chipRowNodeRefs.current = new Map();
  }, [sessionKeysSignature, weekDates, density, windowStart, windowEnd]);

  // Panel open/close changes column width; detailed cards wrap taller and must remasure.
  useEffect(() => {
    if (isCompact) {
      return undefined;
    }
    const node = weekRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    weekWidthRef.current = node.getBoundingClientRect().width;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0] && entries[0].contentRect ? entries[0].contentRect.width : 0;
      if (Math.abs(width - weekWidthRef.current) < 1) {
        return;
      }
      weekWidthRef.current = width;
      setMeasuredHeights({});
      setMeasuredChipRowHeights({});
      setMeasureEpoch((value) => value + 1);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isCompact]);

  const groupsByDate = useMemo(() => {
    const map = new Map();
    weekDates.forEach((date) => {
      const items = layoutDaySessions(
        sessionsByDate.get(date) || [],
        windowStart,
        windowEnd,
        density,
        measuredHeights
      );
      map.set(
        date,
        isCompact
          ? buildCompactChipGroups(items, date, measuredChipRowHeights)
          : buildDetailedTimeGroups(items, date, measuredChipRowHeights)
      );
    });
    return map;
  }, [
    isCompact,
    weekDates,
    sessionsByDate,
    windowStart,
    windowEnd,
    density,
    measuredHeights,
    measuredChipRowHeights,
  ]);

  const hourHeights = useMemo(() => {
    if (hours.length === 0) {
      return null;
    }
    return resolveCompactHourHeightsPx(hours, groupsByDate);
  }, [hours, groupsByDate]);

  const layoutsByDate = useMemo(() => {
    const map = new Map();
    weekDates.forEach((date) => {
      map.set(
        date,
        placeCompactChipGroups(groupsByDate.get(date) || [], windowStart, hourHeights, hours)
      );
    });
    return map;
  }, [weekDates, groupsByDate, windowStart, hourHeights, hours]);

  const dayHeight = useMemo(() => {
    let total = 0;
    hours.forEach((hour) => {
      total += (hourHeights && hourHeights.get(hour)) || WEEK_ROW_HEIGHT_PX;
    });
    layoutsByDate.forEach((layout) => {
      layout.forEach((entry) => {
        total = Math.max(total, entry.top + entry.height);
      });
    });
    return total;
  }, [hours, layoutsByDate, hourHeights]);
  const isEmptyWeek = !displayWindow;

  useLayoutEffect(() => {
    const nextRows = {};
    let rowsChanged = false;
    chipRowNodeRefs.current.forEach((node, key) => {
      if (!node) {
        return;
      }
      const list = node.querySelector('.cv2-week__chip-list, .cv2-week__time-group-cards');
      const prevHeight = node.style.height;
      const prevMinHeight = node.style.minHeight;
      node.style.height = 'auto';
      node.style.minHeight = '0';
      const measured = Math.ceil(
        Math.max(node.scrollHeight, list ? list.scrollHeight + 4 : 0)
      );
      node.style.height = prevHeight;
      node.style.minHeight = prevMinHeight;
      const previous = measuredChipRowHeights[key] || 0;
      if (Math.abs(measured - previous) > 1) {
        nextRows[key] = measured;
        rowsChanged = true;
      }
    });
    if (rowsChanged) {
      setMeasuredChipRowHeights((prev) => ({ ...prev, ...nextRows }));
    }

    if (isCompact) {
      return undefined;
    }

    const next = {};
    let changed = false;

    sessionNodeRefs.current.forEach((node, key) => {
      if (!node) {
        return;
      }
      const card = node.querySelector('.cv2-card');
      if (!card) {
        return;
      }
      // Lift the fixed band height so wrap-taller content (e.g. after panel open) is measurable.
      const prevHeight = node.style.height;
      node.style.height = 'auto';
      const measured = Math.ceil(Math.max(card.scrollHeight, card.offsetHeight));
      node.style.height = prevHeight;
      const previous = measuredHeights[key] || 0;
      if (Math.abs(measured - previous) > 1) {
        next[key] = measured;
        changed = true;
      }
    });

    if (changed) {
      setMeasuredHeights((prev) => ({ ...prev, ...next }));
    }

    return undefined;
  }, [layoutsByDate, dayHeight, measuredHeights, measuredChipRowHeights, isCompact, measureEpoch]);

  function bindSessionNode(key) {
    return (node) => {
      if (node) {
        sessionNodeRefs.current.set(key, node);
      } else {
        sessionNodeRefs.current.delete(key);
      }
    };
  }

  function bindChipRowNode(key) {
    return (node) => {
      if (node) {
        chipRowNodeRefs.current.set(key, node);
      } else {
        chipRowNodeRefs.current.delete(key);
      }
    };
  }

  function hourOffset(time) {
    return offsetAtHour(time, windowStart, hourHeights, hours);
  }

  function hourRowHeight(hour) {
    if (hourHeights) {
      return hourHeights.get(hour) || WEEK_ROW_HEIGHT_PX;
    }
    return WEEK_ROW_HEIGHT_PX;
  }

  const todayInWeek = weekDates.includes(todayDate);

  useLayoutEffect(() => {
    const scroller = weekRef.current;
    if (!scroller || isEmptyWeek || !todayInWeek) {
      return undefined;
    }

    const timezone = sessionStorage.getItem('timezone_identifier');
    const now = timezone ? Moment().tz(timezone) : Moment();
    const nowHour = now.hour() + now.minute() / 60 + now.second() / 3600;
    const todayLayout = layoutsByDate.get(todayDate) || [];
    const focusGroup = pickFocusTimeGroup(todayLayout, nowHour);
    const clamped = Math.min(Math.max(nowHour, windowStart), windowEnd - 0.01);
    const targetTop = focusGroup ? focusGroup.top : hourOffset(clamped);
    const scrollKey = `${todayDate}|${weekDates[0]}|${density}|${Math.round(targetTop)}`;
    if (autoScrollKeyRef.current === scrollKey) {
      return undefined;
    }

    const maxScroll = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollTop = Math.min(Math.max(0, targetTop - 8), maxScroll);

    const todayColumn = scroller.querySelector('.cv2-week__day--today');
    if (todayColumn) {
      const scrollerRect = scroller.getBoundingClientRect();
      const columnRect = todayColumn.getBoundingClientRect();
      const timeColumnWidth = 64;
      const leftEdge = scrollerRect.left + timeColumnWidth;
      if (columnRect.left < leftEdge || columnRect.right > scrollerRect.right) {
        scroller.scrollLeft = Math.max(
          0,
          scroller.scrollLeft + columnRect.left - leftEdge
        );
      }
    }

    autoScrollKeyRef.current = scrollKey;
    return undefined;
  }, [
    isEmptyWeek,
    todayInWeek,
    todayDate,
    weekDates,
    density,
    windowStart,
    windowEnd,
    hourHeights,
    hours,
    layoutsByDate,
  ]);

  return (
    <div
      className={`cv2-week${isCompact ? ' cv2-week--compact' : ' cv2-week--detailed'}`}
      ref={weekRef}
    >
      <div className="cv2-week__grid">
        <div className="cv2-week__head">Time</div>
        {weekDates.map((date) => {
          const moment = Moment(date, 'YYYY-MM-DD');
          const stats = dayStatsByDate.get(date);
          return (
            <div
              className={`cv2-week__head${date === todayDate ? ' cv2-week__head--today' : ''}`}
              key={date}
            >
              <span className="cv2-week__head-day">{moment.format('ddd DD/MM')}</span>
              {stats ? (
                <span className="cv2-week__head-meta">
                  {stats.sessionCount} session{stats.sessionCount === 1 ? '' : 's'} ·{' '}
                  {stats.lessonCount} student{stats.lessonCount === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
          );
        })}

        {isEmptyWeek ? (
          <div className="cv2-week__empty" role="status">
            No sessions this week
          </div>
        ) : (
          <>
        <div className="cv2-week__times" style={{ height: dayHeight }}>
          {hours.map((hour) => (
            <div
              className="cv2-week__time"
              key={hour}
              style={{ height: hourRowHeight(hour) }}
            >
              {hourLabel(hour)}
            </div>
          ))}
        </div>

        {weekDates.map((date) => {
          const isWeekend = WEEKEND_DAY_NUMBERS.includes(Moment(date, 'YYYY-MM-DD').day());
          const layout = layoutsByDate.get(date) || [];

          return (
            <div
              className={[
                'cv2-week__day',
                isWeekend ? 'cv2-week__day--weekend' : '',
                date === todayDate ? 'cv2-week__day--today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={date}
              style={{ height: dayHeight }}
            >
              {hours.map((hour) => (
                <div
                  className="cv2-week__hour-line"
                  key={`${date}-${hour}`}
                  style={{
                    top: hourOffset(hour),
                    height: hourRowHeight(hour),
                  }}
                />
              ))}

              {date === nowMarker.date &&
              nowMarker.hour >= windowStart &&
              nowMarker.hour < windowEnd ? (
                <div
                  className="cv2-week__now"
                  style={{ top: hourOffset(nowMarker.hour) }}
                  aria-hidden
                />
              ) : null}

              {isCompact
                ? layout.map((group) => (
                    <div
                      className="cv2-week__chip-row"
                      key={`${date}-${group.key}`}
                      ref={bindChipRowNode(`${date}|${group.key}`)}
                      style={{
                        top: group.top,
                        minHeight: group.height,
                        height: group.height,
                        left: 2,
                        right: 2,
                        maxWidth: 'calc(100% - 4px)',
                      }}
                    >
                      <span className="cv2-week__chip-time">
                        {formatChipTimeLabel(group.timeLabel, group.start)}
                      </span>
                      <div className="cv2-week__chip-list">
                        {group.items.map(({ session }) => (
                          <div
                            className="cv2-week__session cv2-week__session--chip"
                            key={session.key}
                            ref={bindSessionNode(session.key)}
                          >
                            <SessionCard
                              session={session}
                              variant={cardVariant}
                              tutorInitials={initialsOf(session.tutorName)}
                              onOpen={onOpenSession}
                              onStatusClick={onStatusClick}
                              onModeClick={onModeClick}
                              onStudentClick={onStudentClick}
                              onTutorClick={onTutorClick}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                : layout.map((group) => (
                    <div
                      className="cv2-week__time-group"
                      key={`${date}-${group.key}`}
                      ref={bindChipRowNode(`${date}|${group.key}`)}
                      style={{
                        top: group.top,
                        minHeight: group.height,
                        height: group.height,
                        left: 2,
                        right: 2,
                        maxWidth: 'calc(100% - 4px)',
                      }}
                    >
                      <span className="cv2-week__chip-time">
                        {formatChipTimeLabel(group.timeLabel, group.start)}
                      </span>
                      <div className="cv2-week__time-group-cards">
                        {group.items.map(({ session, minCardPx }) => (
                          <div
                            className="cv2-week__session cv2-week__session--grouped"
                            key={session.key}
                            ref={bindSessionNode(session.key)}
                            style={{ height: minCardPx }}
                          >
                            <SessionCard
                              session={session}
                              variant={cardVariant}
                              tutorInitials={initialsOf(session.tutorName)}
                              showTutorName
                              onOpen={onOpenSession}
                              onStatusClick={onStatusClick}
                              onModeClick={onModeClick}
                              onStudentClick={onStudentClick}
                              onTutorClick={onTutorClick}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
            </div>
          );
        })}
          </>
        )}
      </div>
    </div>
  );
}

WeekView.propTypes = {
  weekDates: PropTypes.arrayOf(PropTypes.string).isRequired,
  sessions: PropTypes.array.isRequired,
  hourWindow: PropTypes.shape({
    startHour: PropTypes.number.isRequired,
    endHour: PropTypes.number.isRequired,
  }).isRequired,
  showFullDay: PropTypes.bool,
  density: PropTypes.string.isRequired,
  todayDate: PropTypes.string.isRequired,
  onOpenSession: PropTypes.func.isRequired,
  onStatusClick: PropTypes.func.isRequired,
  onModeClick: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
};

WeekView.defaultProps = {
  showFullDay: false,
};
